import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comicList } from '@/mocks/comics';
import { categories } from '@/mocks/categories';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comic = comicList.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900">漫画未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:underline">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comicList
    .filter((c) => c.id !== comic.id && c.tags.some((t) => comic.tags.includes(t)))
    .slice(0, 4);

  const category = categories.find((c) => c.slug === comic.tags[0] === '恋爱' ? 'love' : comic.tags[0] === '动作' ? 'action' : comic.tags[0] === '奇幻' ? 'fantasy' : comic.tags[0] === '悬疑' ? 'suspense' : 'action');

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `http://h8wx1.com/article/${comic.id}`,
    headline: `${comic.title} - 韩国漫画全集免费在线阅读 | 韩漫网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2026-01-15',
    dateModified: comic.updatedAt,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '韩漫网',
      url: 'http://h8wx1.com/',
    },
    about: {
      '@type': 'Thing',
      name: `${comic.tags.join('、')}韩漫`,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://h8wx1.com/article/${comic.id}`,
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}是什么类型的韩国漫画？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}是一部${comic.tags.join('、')}类型的韩国漫画，由${comic.author}创作，目前${comic.status}，共${comic.chapters}话。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}在哪里可以免费在线阅读？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以在韩漫网免费在线阅读${comic.title}全集，支持下拉式浏览，画质高清，无需付费订阅。`,
        },
      },
    ],
  };

  const articleTitle = `${comic.title}_${comic.author}_韩国漫画全集免费在线阅读-韩漫网`;
  const articleDesc = `${comic.title}是由${comic.author}创作的${comic.tags.join('、')}类型韩国漫画，${comic.status}，共${comic.chapters}话。${comic.description}韩漫网提供${comic.title}全集免费下拉式在线阅读。`;
  const articleKeywords = `${comic.title},韩国漫画,${comic.author},韩漫网,${comic.tags.slice(0, 2).join(',')}`;
  const articleUrl = `http://h8wx1.com/article/${comic.id}`;

  return (
    <>
      <title>{articleTitle}</title>
      <meta name="description" content={articleDesc} />
      <meta name="keywords" content={articleKeywords} />
      <link rel="canonical" href={articleUrl} />
      <meta name="last-modified" content={comic.updatedAt} />
      <meta property="og:title" content={articleTitle} />
      <meta property="og:description" content={articleDesc} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={articleUrl} />
      <meta property="og:image" content={comic.cover} />
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={articleTitle} />
      <meta name="twitter:description" content={articleDesc} />
      <meta name="twitter:image" content={comic.cover} />
      <Navbar />
      <main>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

        {/* Breadcrumb */}
        <div className="pt-16 bg-background-50">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: category?.name || '漫画', path: `/category/${category?.slug || 'action'}` },
              { name: comic.title },
            ]}
          />
        </div>

        {/* Article Hero */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              {/* Cover */}
              <div className="lg:w-80 flex-shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩国漫画全集免费在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                  <span
                    className={`px-3 py-1 text-xs rounded-full whitespace-nowrap ${
                      comic.status === '连载中'
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}
                  >
                    {comic.status}
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-4xl text-foreground-950">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  作者：{comic.author}
                </p>

                <div className="flex flex-wrap items-center gap-4 mt-4">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-primary-400"></i>
                    <span className="text-sm font-semibold text-foreground-900">
                      {comic.rating}
                    </span>
                    <span className="text-xs text-foreground-400">评分</span>
                  </div>
                  <span className="text-foreground-200">|</span>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-eye-line text-foreground-400"></i>
                    <span className="text-sm text-foreground-700 whitespace-nowrap">
                      {(comic.views / 10000).toFixed(0)}万
                    </span>
                    <span className="text-xs text-foreground-400">阅读</span>
                  </div>
                  <span className="text-foreground-200">|</span>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-file-list-3-line text-foreground-400"></i>
                    <span className="text-sm text-foreground-700 whitespace-nowrap">
                      共{comic.chapters}话
                    </span>
                  </div>
                </div>

                <div className="mt-6 p-5 bg-background-100 rounded-lg border border-background-200/50">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                    作品简介
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href="#chapters"
                    className="px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full whitespace-nowrap hover:bg-primary-600 transition-colors duration-300 cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-play-circle-line"></i>
                    开始阅读
                  </a>
                  <button className="px-6 py-3 bg-background-100 text-foreground-700 text-sm font-medium rounded-full whitespace-nowrap hover:bg-background-200 transition-colors duration-300 cursor-pointer flex items-center gap-2 border border-background-200/50">
                    <i className="ri-bookmark-line"></i>
                    加入收藏
                  </button>
                  <button className="px-6 py-3 bg-background-100 text-foreground-700 text-sm font-medium rounded-full whitespace-nowrap hover:bg-background-200 transition-colors duration-300 cursor-pointer flex items-center gap-2 border border-background-200/50">
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapters */}
        <section id="chapters" className="w-full py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">
              章节列表
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Array.from({ length: Math.min(comic.chapters, 12) }).map((_, i) => {
                const chNum = comic.chapters - i;
                return (
                  <a
                    key={chNum}
                    href="#"
                    className="flex items-center justify-between bg-background-50 rounded-lg px-5 py-3.5 border border-background-200/50 hover:border-primary-300/50 transition-all duration-200 cursor-pointer"
                    onClick={(e) => e.preventDefault()}
                  >
                    <span className="text-sm text-foreground-800 whitespace-nowrap">
                      第{chNum}话
                    </span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">
                      {i === 0 ? '最新' : ''}
                    </span>
                  </a>
                );
              })}
            </div>

            {comic.chapters > 12 && (
              <div className="text-center mt-6">
                <span className="text-sm text-foreground-400">
                  还有{comic.chapters - 12}话内容...
                </span>
              </div>
            )}
          </div>
        </section>

        {/* Related */}
        {relatedComics.length > 0 && (
          <section className="w-full py-10 md:py-14 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {relatedComics.map((c) => (
                  <Link
                    key={c.id}
                    to={`/article/${c.id}`}
                    className="group block bg-background-100 rounded-lg overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-[1.02]"
                  >
                    <div className="aspect-[3/4] overflow-hidden">
                      <img
                        src={c.cover}
                        alt={c.title}
                        title={`${c.title} - 韩国漫画在线阅读`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-foreground-900 whitespace-nowrap overflow-hidden text-ellipsis">
                        {c.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{c.author}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="w-full py-14 bg-secondary-500">
          <div className="w-full px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl text-background-50">
              更多精彩韩漫等你发现
            </h2>
            <p className="mt-3 text-sm text-background-50/70">
              浏览更多分类，探索不同风格的韩国漫画
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/category/love"
                className="px-5 py-2.5 bg-background-50/20 text-background-50 text-sm rounded-full whitespace-nowrap hover:bg-background-50/30 transition-all duration-300 cursor-pointer"
              >
                恋爱韩漫
              </Link>
              <Link
                to="/category/action"
                className="px-5 py-2.5 bg-background-50/20 text-background-50 text-sm rounded-full whitespace-nowrap hover:bg-background-50/30 transition-all duration-300 cursor-pointer"
              >
                动作韩漫
              </Link>
              <Link
                to="/category/fantasy"
                className="px-5 py-2.5 bg-background-50/20 text-background-50 text-sm rounded-full whitespace-nowrap hover:bg-background-50/30 transition-all duration-300 cursor-pointer"
              >
                奇幻韩漫
              </Link>
              <Link
                to="/"
                className="px-5 py-2.5 bg-background-50/20 text-background-50 text-sm rounded-full whitespace-nowrap hover:bg-background-50/30 transition-all duration-300 cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}